import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { IconButton, CircularProgress, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

const EstimatesGrid = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userMap, setUserMap] = useState({});
  const navigate = useNavigate();

  const handleEditClick = useCallback(
    (estimateId) => {
      navigate(`/estimates/continue/${estimateId}`);
    },
    [navigate]
  );

  const columnDefs = [
    {
      headerName: 'Actions',
      field: 'id',
      width: 80,
      pinned: 'left',
      suppressMenu: true,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton onClick={() => handleEditClick(params.value)} title="Edit">
            <EditIcon />
          </IconButton>
        </div>
      ),
    },
    { headerName: 'Project Name', field: 'projectName' },
    { headerName: 'Requested By', field: 'requestedBy' },
    { headerName: 'Estimated By', field: 'estimatedBy' },
    {
      headerName: 'Estimate Date',
      field: 'estimateDate',
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString() : ''),
    },
    {
      headerName: 'Last Modified By',
      field: 'lastModifiedBy',
      valueFormatter: ({ value }) => (value && userMap[value] ? userMap[value] : 'Unknown'),
    },
    {
      headerName: 'Last Updated',
      field: 'lastUpdated',
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [estimatesRes, usersRes] = await Promise.all([
          axios.get('/api/PipelinePricing/pipeline/get-all-estimates'),
          axios.get('/api/Admin/users'),
        ]);

        // Create a map: userId -> user.name
        const map = {};
        usersRes.data.forEach((user) => {
          map[user.id] = user.name;
        });

        setUserMap(map);
        setRowData(estimatesRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box
      sx={{
        borderRadius: 2,
        boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        backgroundColor: '#fff',
        height: '100%',
      }}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="ag-theme-alpine" style={{ width: '100%' }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            defaultColDef={{
              flex: 1,
              minWidth: 150,
              resizable: true,
              sortable: true,
              filter: true,
              cellStyle: {
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </div>
      )}
    </Box>
  );
};

export default EstimatesGrid;
